import React, { useState, useEffect } from 'react';
import client from '../api/client';

export default function TwoFactorSetupPage({ onClose }) {
  const [step, setStep]             = useState('init'); // init | scan | backup | done
  const [qrBase64, setQrBase64]     = useState('');
  const [secret, setSecret]         = useState('');
  const [code, setCode]             = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    if (step === 'init') startSetup();
  }, []);

  // ESC キーでモーダルを閉じる（backup 表示中は誤操作防止のため無効）
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && step !== 'backup') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step, onClose]);

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await client.post('/auth/2fa/setup');
      setQrBase64(data.qr_base64);
      setSecret(data.secret);
      setStep('scan');
    } catch (err) {
      setError(err.response?.data?.detail ?? 'セットアップに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async (e) => {
    e.preventDefault();
    if (!code) { setError('コードを入力してください'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await client.post('/auth/2fa/enable', { code });
      setBackupCodes(data.backup_codes);
      setStep('backup');
    } catch (err) {
      setError(err.response?.data?.detail ?? '認証に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (loading && step === 'init') {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <span className="spinner spinner-dark" style={{ width: 28, height: 28 }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        {step === 'scan' && (
          <>
            <div style={headerStyle}>
              <div style={{ fontSize: 32 }}>📱</div>
              <div style={titleStyle}>2段階認証のセットアップ</div>
              <div style={subStyle}>
                Google Authenticator / Authy などで QR コードをスキャンしてください
              </div>
            </div>

            {qrBase64 && (
              <div style={{ textAlign: 'center', margin: '20px 0' }}>
                <img src={`data:image/png;base64,${qrBase64}`} alt="QR Code"
                  style={{ width: 200, height: 200, border: '1px solid #eee', borderRadius: 8 }} />
              </div>
            )}

            <details style={{ marginBottom: 16, fontSize: 12, color: '#888' }}>
              <summary style={{ cursor: 'pointer' }}>QR コードが読み取れない場合</summary>
              <div style={{ marginTop: 8, wordBreak: 'break-all', fontFamily: 'monospace', background: '#f5f5f5', padding: 8, borderRadius: 6 }}>
                {secret}
              </div>
            </details>

            <form onSubmit={handleEnable} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">アプリに表示された6桁のコードで確認</label>
                <input className="form-input" type="text" inputMode="numeric" maxLength={6}
                  placeholder="123456" value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                  style={{ textAlign: 'center', fontSize: 22, letterSpacing: '0.3em', fontWeight: 700 }}
                />
              </div>
              {error && <div className="msg msg-error">{error}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>
                  キャンセル
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={loading}>
                  {loading ? <><span className="spinner" />有効化中…</> : '有効化'}
                </button>
              </div>
            </form>
          </>
        )}

        {step === 'backup' && (
          <>
            <div style={headerStyle}>
              <div style={{ fontSize: 32 }}>🛡️</div>
              <div style={titleStyle}>バックアップコード</div>
              <div style={subStyle}>
                認証アプリが使えない場合に使用できます。安全な場所に保管してください。<br />
                <strong>各コードは1回のみ使用可能です。</strong>
              </div>
            </div>

            <div className="grid-2" style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
              margin: '20px 0', padding: 16,
              background: '#f8f5ff', borderRadius: 10, border: '1px solid #e0d5f7',
            }}>
              {backupCodes.map((c, i) => (
                <div key={i} style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: '#5f27cd' }}>
                  {c}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn btn-secondary"
                style={{ flex: 1 }}
                onClick={() => {
                  navigator.clipboard.writeText(backupCodes.join('\n'));
                }}
              >
                コピー
              </button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setStep('done')}>
                保存しました
              </button>
            </div>
          </>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={titleStyle}>2段階認証が有効になりました</div>
            <div style={{ ...subStyle, marginBottom: 24 }}>
              次回のログインから認証コードが必要になります
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={onClose}>
              閉じる
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(0,0,0,0.45)',
};
const cardStyle = {
  background: '#fff', borderRadius: 24, padding: '40px 36px',
  width: 440, maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
};
const headerStyle = { textAlign: 'center', marginBottom: 20 };
const titleStyle = {
  fontFamily: "'Nunito', 'M PLUS Rounded 1c', sans-serif",
  fontSize: 20, fontWeight: 900, color: '#7048D4', marginTop: 8,
};
const subStyle = { fontSize: 13, color: '#888', marginTop: 8, lineHeight: 1.6 };
